import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import FeedbackButton from '../feedback/FeedbackButton';
import { 
  FileText, 
  Play, 
  PlusCircle, 
  Clock, 
  CalendarDays,
  ChevronRight,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

const QuickAccessWorkouts = ({ loading, workoutPlans = [] }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  
  // Format date string
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };
  
  return (
    <div className={`p-5 rounded-xl shadow-sm ${
      isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
    }`}>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <FileText className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('standaloneDashboard.workoutPlans.title')}
          </h3>
        </div>
        
        <Link
          to="/standalone/visualizza-schede"
          className="flex items-center text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
        >
          {t('standaloneDashboard.viewAll')}
          <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
        </Link>
      </div>
      
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          ))}
        </div>
      ) : workoutPlans.length > 0 ? (
        <div className="space-y-3">
          {workoutPlans.slice(0, 3).map((plan) => (
            <div 
              key={plan.id}
              className={`rounded-lg overflow-hidden border ${
                isDark 
                  ? 'border-gray-700 hover:border-gray-600 bg-gray-800' 
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              } transition-colors`}
            >
              <div className="p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">{plan.nome}</h4>
                
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-3">
                  <CalendarDays className="h-3.5 w-3.5 mr-1" />
                  <span>{t('workout.createdOn')} {formatDate(plan.data_creazione)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[150px]">
                    {plan.descrizione || t('standaloneDashboard.noDescription')}
                  </p>
                  
                  <div className="flex space-x-2">
                    <Link
                      to={`/standalone/modifica-scheda/${plan.id}`}
                      className={`p-1.5 rounded ${
                        isDark 
                          ? 'hover:bg-gray-700 text-blue-400' 
                          : 'hover:bg-gray-100 text-blue-600'
                      }`}
                      title={t('common.edit')}
                    >
                      <FileText className="h-4 w-4" />
                    </Link>
                    

                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {workoutPlans.length > 3 && (
            <Link
              to="/standalone/visualizza-schede"
              className={`flex items-center justify-center p-3 rounded-lg border ${
                isDark 
                  ? 'border-gray-700 hover:bg-gray-700 text-gray-300' 
                  : 'border-gray-200 hover:bg-gray-50 text-gray-700'
              } transition-colors text-sm`}
            >
              {t('standaloneDashboard.viewMorePlans', { count: workoutPlans.length - 3 })}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          )}
        </div>
      ) : (
        <div className="py-6 text-center">
          <AlertCircle className="h-10 w-10 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            {t('standaloneDashboard.noWorkoutPlans')}
          </p>
          <Link
            to="/standalone/crea-scheda"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 rounded-lg text-white font-medium hover:bg-indigo-700 transition-colors"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            {t('standaloneDashboard.workoutPlans.createNew')}
          </Link>
        </div>
      )}
      
      {/* Quick links */}
      <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t('standaloneDashboard.quickLinks')}
        </h4>
        
        <div className="space-y-2">
          <Link
            to="/standalone/crea-scheda"
            className={`flex items-center p-2 rounded-lg transition-colors ${
              isDark 
                ? 'hover:bg-gray-700 text-gray-200' 
                : 'hover:bg-gray-100 text-gray-800'
            }`}
          >
            <PlusCircle className="h-5 w-5 mr-3 text-indigo-600 dark:text-indigo-400" />
            <span className="text-sm">{t('standaloneDashboard.workoutPlans.createNew')}</span>
          </Link>
          

                    
        <FeedbackButton />
        </div>
      </div>
    </div>
  );
};

export default QuickAccessWorkouts;